import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { UserInfoCard } from './components/UserInfoCard';
import { AIModeSection } from './components/AIModeSection';
import { DailyGoalToggle } from './components/DailyGoalToggle';
import { UserProfileSection } from './components/UserProfileSection';
import { RoutineSettingsPanel } from './components/RoutineSettingsPanel';
import { MembershipCard } from './components/MembershipCard';
import { SettingsList } from './components/SettingsList';
import { LanguageSwitcher } from '../../components/layout/LanguageSwitcher';
import { Header } from '../../components/layout/Header';

export const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user, loading, isPlus } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (import.meta.env.DEV && localStorage.getItem('dev_preview') === '1') return;
    if (!loading && !user) {
      navigate('/onboarding');
    }
  }, [user, loading, navigate]);

  const devPreview = import.meta.env.DEV && localStorage.getItem('dev_preview') === '1';
  if (!devPreview && (loading || !user)) return null;

  return (
    <div className="relative flex h-full items-center justify-center bg-transparent px-0 md:px-8">
      <div className="pointer-events-none fixed right-0 top-0 h-[280px] w-[280px] rounded-full bg-[#B2EEDA]/10 blur-[90px]" />
      <div className="pointer-events-none fixed bottom-[120px] left-0 h-[200px] w-[200px] rounded-full bg-rose-200/20 blur-[70px]" />

      <div className="app-mobile-page-frame app-scroll-container profile-page-typography relative h-full w-full max-w-[430px] text-slate-900 [box-shadow:0_0_0_1px_rgba(0,0,0,0.06),0_24px_64px_rgba(0,0,0,0.1)] md:h-[calc(100%-24px)] md:max-w-[980px] md:rounded-[30px] md:border md:border-white/70 md:bg-[#fcfaf7]/85 md:[box-shadow:0_0_0_1px_rgba(255,255,255,0.45),0_24px_64px_rgba(15,23,42,0.12)]">
        <Header className="sticky top-0 z-10 px-4 pb-3 pt-11">
          <h1 className="app-page-title text-[#1e293b]">{t('nav_profile')}</h1>
        </Header>

        <div className="space-y-3 px-3 py-3 pb-28">
          <UserInfoCard isPlus={isPlus} />
          <div className="overflow-visible">
            <div>
              <AIModeSection isPlus={isPlus} plain />
            </div>
            {!isPlus ? (
              <div className="px-0 py-2">
                <MembershipCard isPlus={isPlus} />
              </div>
            ) : null}
            <div>
              <DailyGoalToggle plain />
            </div>
            <div>
              <RoutineSettingsPanel plain />
            </div>
            {isPlus ? (
              <div>
                <UserProfileSection plain />
              </div>
            ) : null}
            {!isPlus ? (
              <div>
                <UserProfileSection plain locked />
              </div>
            ) : null}
            <div className="px-4 py-3 transition hover:bg-white/70">
              <div className="flex w-full items-center justify-between gap-3">
                <div className="flex items-center space-x-2.5">
                  <Languages size={18} strokeWidth={2} className="text-[#000000]" />
                  <span className="app-item-title text-black">{t('language_switch')}</span>
                </div>
                <LanguageSwitcher variant="list" />
              </div>
            </div>
            <div>
              <SettingsList plain />
            </div>
          </div>
          <div className="h-2" />
        </div>
      </div>
    </div>
  );
};
